import { useCallback, useEffect, useState } from 'react';

export type ThemePreference = 'auto' | 'light' | 'dark';

const STORAGE_KEY = 'piwnica-theme';

/** The same key the pre-paint script in public/index.html reads. */
function readStored(): ThemePreference {
  try {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved === 'light' || saved === 'dark' || saved === 'auto') return saved;
  } catch {
    /* storage blocked (private mode) -- follow the OS */
  }
  return 'auto';
}

function apply(preference: ThemePreference) {
  const root = document.documentElement;
  if (preference === 'auto') {
    // No stamp at all: prefers-color-scheme decides, which is the whole point
    // of having an "auto" state rather than just a light/dark switch.
    root.removeAttribute('data-theme');
  } else {
    root.setAttribute('data-theme', preference);
  }
}

/**
 * Three-state theme preference: auto (follow the OS), light, dark.
 *
 * `resolved` is what is actually on screen, which is what the toggle's label
 * needs to describe -- under "auto" that depends on the OS and can change while
 * the app is open.
 */
export function useTheme() {
  const [preference, setPreference] = useState<ThemePreference>(readStored);
  const [systemDark, setSystemDark] = useState(
    () => window.matchMedia?.('(prefers-color-scheme: dark)').matches ?? false
  );

  useEffect(() => {
    apply(preference);
    try {
      localStorage.setItem(STORAGE_KEY, preference);
    } catch {
      /* nothing to do -- the preference just will not survive a reload */
    }
  }, [preference]);

  // The OS can flip while the app is open (a sunset schedule, for instance).
  useEffect(() => {
    const query = window.matchMedia?.('(prefers-color-scheme: dark)');
    if (!query) return;
    const onChange = (event: MediaQueryListEvent) => setSystemDark(event.matches);
    query.addEventListener('change', onChange);
    return () => query.removeEventListener('change', onChange);
  }, []);

  const cycle = useCallback(() => {
    setPreference((current) =>
      current === 'auto' ? 'light' : current === 'light' ? 'dark' : 'auto'
    );
  }, []);

  const resolved: 'light' | 'dark' =
    preference === 'auto' ? (systemDark ? 'dark' : 'light') : preference;

  return { preference, resolved, setPreference, cycle };
}
